import { SerializedStyles } from "@emotion/react";
import { Ref, memo, useCallback } from "react";
import { usePartialSubmit } from "src/apps/AppFlow/pages/hooks";
import { FormFlowPage, IllustrativeOptions, TextInput } from "src/components";
import { createStyles } from "src/styles";
import { UseOfFundsValue } from "src/types/Application";

import { enums, formatters } from "@fraction/shared";

const { USE_OF_FUNDS_MAP } = formatters.enums;
const USE_OF_FUNDS_OPTIONS: enums.UseOfFunds[] = [
  enums.UseOfFunds.DEBT_PAYDOWN,
  enums.UseOfFunds.PROPERTY_PURCHASE,
  enums.UseOfFunds.INVESTING,
  enums.UseOfFunds.MEDICAL_EXPENSES,
  enums.UseOfFunds.OTHER,
];

export interface UseOfFundsProps {
  onChange: (value: UseOfFundsValue) => void;
  value?: UseOfFundsValue;

  focusRef?: Ref<HTMLInputElement>;
  onNext: () => void;
  onBack: () => void;
  style?: SerializedStyles;
}

const styles = createStyles({
  options: {
    marginBottom: "24px",
  },
  noteTextarea: {
    height: 120,
    marginBottom: 24,
  },
});

const UseOfFunds = ({ onNext, onBack, style, focusRef, value, onChange, ...props }: UseOfFundsProps) => {
  const validate = useCallback(
    () =>
      !!value?.choices?.length && !(value?.choices?.includes(enums.UseOfFunds.OTHER) && !value?.description),
    [value]
  );
  const { handleNext, nextDisabled, nextLoading } = usePartialSubmit({ onNext, validate });

  const handleChangeChoices = useCallback(
    (choices: enums.UseOfFunds[]) => {
      onChange({
        ...value,
        choices,
      });
    },
    [value, onChange]
  );

  const handleChangeDescription = useCallback(
    (description: string) => {
      onChange({
        ...value,
        description,
      });
    },
    [value, onChange]
  );

  return (
    <FormFlowPage
      {...props}
      title="How do you intend to use the funds?"
      description={
        `You can choose as many answers as you'd like. ` +
        `Note if you choose "Other", we ask that you also write notes below. `
      }
      onNext={handleNext}
      onBack={onBack}
      style={style}
      nextDisabled={nextDisabled}
      nextLoading={nextLoading}
    >
      <IllustrativeOptions
        label="Use of funds"
        placeholder="Pick as many as you want"
        isMulti
        focusRef={focusRef}
        value={value?.choices}
        onChange={handleChangeChoices}
        options={USE_OF_FUNDS_OPTIONS.map((type) => ({
          value: type,
          label: USE_OF_FUNDS_MAP[type],
        }))}
        containerStyle={styles.options}
      />
      <TextInput
        value={value?.description}
        onChange={handleChangeDescription}
        label="Additional notes"
        placeholder="e.g. Providing a downpayment for my child."
        multiline
        style={styles.noteTextarea}
      />
    </FormFlowPage>
  );
};

export default memo(UseOfFunds);
